import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Send, FileText, Clock, Settings } from "lucide-react";
import { Link } from "react-router-dom";

const TYPE_LABEL = { morning: "Matin", afternoon: "Après-midi", custom: "Manuel" };

const pad2 = (n) => String(n).padStart(2, "0");

function utcToLocalLabel(hour, minute) {
  const now = new Date();
  const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute));
  return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

export default function Summaries() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("custom");
  const [slots, setSlots] = useState(null); // null = loading

  const load = async () => {
    try {
      const r = await api.get("/summaries");
      setItems(r.data);
      if (r.data.length && !selected) setSelected(r.data[0]);
    } catch (err) { toast.error("Erreur"); }
  };

  useEffect(() => {
    load();
    api.get("/settings/digest-schedule")
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]));
    /* eslint-disable-next-line */
  }, []);

  const generate = async (send) => {
    setLoading(true);
    try {
      const r = await api.post("/summaries/generate", null, { params: { digest_type: type, send } });
      toast.success(send ? `Résumé envoyé à ${r.data.sent_to}` : "Résumé généré");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  // Build schedule description
  const scheduleLabel = (() => {
    if (slots === null) return "Chargement…";
    const active = (slots || []).filter((s) => s.enabled);
    if (active.length === 0) {
      return "Aucun créneau actif — configurez-les dans les Paramètres.";
    }
    return active
      .map((s) => `${pad2(s.hour)}h${pad2(s.minute)} UTC (≈ ${utcToLocalLabel(s.hour, s.minute)} locale)`)
      .join(", ");
  })();

  return (
    <div className="p-6 md:p-8" data-testid="summaries-page">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="overline mb-1">// IA Digests</div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            Résumés IA
          </h1>
          {/* Dynamic schedule description */}
          <div className="flex items-center gap-2 mt-1.5">
            <Clock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <p className="text-gray-600 text-sm">{scheduleLabel}</p>
            <Link
              to="/settings"
              className="inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline ml-1 shrink-0"
              title="Modifier les horaires"
            >
              <Settings className="w-3 h-3" /> Modifier
            </Link>
          </div>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            data-testid="digest-type-select"
            className="h-10 px-3 border border-gray-300 bg-white text-sm focus:border-[#002FA7] outline-none"
          >
            <option value="custom">Manuel (24h)</option>
            <option value="morning">Matin (15h01 J-1 → 8h59)</option>
            <option value="afternoon">Après-midi (9h01 → 14h59)</option>
          </select>
          <button
            onClick={() => generate(false)}
            disabled={loading}
            data-testid="generate-summary-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 hover:border-gray-900 text-sm font-medium transition-colors disabled:opacity-50"
          >
            <Sparkles className="w-4 h-4" /> Générer
          </button>
          <button
            onClick={() => generate(true)}
            disabled={loading}
            data-testid="send-summary-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-[#002FA7] hover:bg-[#002277] text-white text-sm font-medium transition-colors disabled:opacity-50"
          >
            <Send className="w-4 h-4" /> Générer &amp; envoyer
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* List */}
        <div className="bg-white border border-gray-200" data-testid="summaries-list">
          <div className="p-4 border-b border-gray-200">
            <div className="overline">// Historique ({items.length})</div>
          </div>
          {items.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">
              Aucun résumé. Générez-en un avec le bouton ci-dessus.
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 max-h-[70vh] overflow-y-auto">
              {items.map((s) => (
                <li
                  key={s.id}
                  onClick={() => setSelected(s)}
                  data-testid={`summary-row-${s.id}`}
                  className={`p-4 cursor-pointer transition-colors ${
                    selected?.id === s.id
                      ? "bg-blue-50 border-l-2 border-[#002FA7]"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-700">
                      {TYPE_LABEL[s.type] || s.type}
                    </span>
                    <span className="text-xs text-gray-500">{s.email_count} emails</span>
                  </div>
                  <div className="text-xs text-gray-500 font-mono">
                    {new Date(s.generated_at).toLocaleString("fr-FR")}
                  </div>
                  {s.sent_to && (
                    <div className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
                      <Send className="w-3 h-3" /> {s.sent_to}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-2 bg-[#F8F9FA] border border-gray-200 p-0">
          {selected ? (
            <div data-testid="summary-detail">
              <div className="flex items-center justify-between p-6 bg-white border-b border-gray-200">
                <div>
                  <div className="overline mb-1">// Briefing {TYPE_LABEL[selected.type] || selected.type}</div>
                  <h3 className="text-xl font-semibold" style={{ fontFamily: "'Work Sans', sans-serif" }}>
                    Du {new Date(selected.generated_at).toLocaleDateString("fr-FR", {
                      weekday: "long", day: "numeric", month: "long",
                    })}
                  </h3>
                  <div className="text-xs text-gray-500 mt-1 font-mono">
                    {new Date(selected.period_start).toLocaleString("fr-FR")} →{" "}
                    {new Date(selected.period_end).toLocaleString("fr-FR")} · {selected.email_count} email
                    {selected.email_count > 1 ? "s" : ""}
                  </div>
                </div>
                <div className="text-sm text-gray-500 flex items-center gap-2 shrink-0">
                  <Clock className="w-4 h-4" />
                  {new Date(selected.generated_at).toLocaleTimeString("fr-FR", {
                    hour: "2-digit", minute: "2-digit",
                  })}
                </div>
              </div>
              <div
                className="p-6 max-h-[75vh] overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: selected.content }}
              />
            </div>
          ) : (
            <div className="text-center text-gray-500 py-20">
              <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <div className="text-sm">Sélectionnez un résumé ou générez-en un nouveau.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
